import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { PurchasedTicket } from '../FavoritesTab';

interface TicketModalProps {
  ticket: PurchasedTicket;
  isOpen: boolean;
  onClose: () => void;
  onCheckedIn?: (ticketId: string) => void;
}

export function TicketModal({ ticket, isOpen, onClose, onCheckedIn }: TicketModalProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [swipeProgress, setSwipeProgress] = useState(0);
  const [isUsed, setIsUsed] = useState(ticket.payment_status === 'checked_in');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineToast, setOfflineToast] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  // オフライン状態の検知
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // リアルタイム時計（1秒ごとに更新）- オフラインでも動作
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // チケットステータスの同期
  useEffect(() => {
    setIsUsed(ticket.payment_status === 'checked_in');
    setSwipeProgress(0);
    setUpdateError(null);
    setShowSuccess(false);
  }, [ticket.payment_status, isOpen]);

  const isValid = ticket.payment_status === 'completed';
  const statusColor = isUsed ? '#10b981' : isValid ? '#f59e0b' : '#ef4444';
  const statusLabel = isUsed ? '使用済み' : isValid ? '有効' : '無効';
  const statusIcon = isUsed ? 'ri-checkbox-circle-fill' : isValid ? 'ri-shield-check-line' : 'ri-close-circle-line';

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${ticket.id}&bgcolor=ffffff`;

  const formatTime = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  };

  // Supabase チェックイン更新（オフライン対応）
  const handleCheckIn = useCallback(async () => {
    if (isUpdating) return;
    setIsUpdating(true);
    setUpdateError(null);

    // オフライン時はローカルで強制的にCRASHED状態にする
    if (isOffline) {
      setIsUsed(true);
      setShowSuccess(true);
      setIsUpdating(false);
      setOfflineToast(true);
      onCheckedIn?.(ticket.id);

      // トースト通知を5秒後に非表示
      setTimeout(() => setOfflineToast(false), 5000);
      return;
    }

    const { error } = await supabase
      .from('event_reservations')
      .update({ payment_status: 'checked_in' })
      .eq('id', ticket.id)
      .eq('payment_status', 'completed'); // 二重チェックイン防止

    if (error) {
      console.error('Check-in update failed:', error);
      setUpdateError('チェックイン処理に失敗しました。もう一度お試しください。');
      setSwipeProgress(0);
      setIsUpdating(false);
      return;
    }

    setIsUsed(true);
    setShowSuccess(true);
    setIsUpdating(false);
    onCheckedIn?.(ticket.id);

    // 成功メッセージを3秒後に非表示
    setTimeout(() => setShowSuccess(false), 3000);
  }, [ticket.id, isUpdating, onCheckedIn, isOffline]);

  // スワイプハンドラー
  const updateSwipeProgress = (clientX: number) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const progress = ((clientX - rect.left) / rect.width) * 100;
    setSwipeProgress(Math.max(0, Math.min(100, progress)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isUsed || isUpdating) return;
    isDragging.current = true;
    updateSwipeProgress(e.clientX);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || isUsed || isUpdating) return;
    updateSwipeProgress(e.clientX);
  };

  const handleMouseUp = () => {
    if (!isDragging.current || isUsed || isUpdating) return;
    isDragging.current = false;

    if (swipeProgress >= 100) {
      handleCheckIn();
    } else {
      setSwipeProgress(0);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isUsed || isUpdating) return;
    isDragging.current = true;
    updateSwipeProgress(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || isUsed || isUpdating) return;
    updateSwipeProgress(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleMouseUp();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/95 backdrop-blur-xl"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="relative w-full max-w-lg bg-gradient-to-b from-gray-900 to-black rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[95vh] overflow-y-auto"
            style={{
              borderTop: `2px solid ${statusColor}40`,
              borderLeft: `1px solid ${statusColor}20`,
              borderRight: `1px solid ${statusColor}20`,
              boxShadow: `0 -10px 80px ${statusColor}30, 0 0 0 1px ${statusColor}15`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md transition-all duration-300 cursor-pointer"
            >
              <i className="ri-close-line text-white text-xl" />
            </button>

            {/* オフライン警告バナー */}
            <AnimatePresence>
              {isOffline && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full overflow-hidden"
                >
                  <motion.div
                    animate={{
                      background: [
                        'linear-gradient(90deg, rgba(251,146,60,0.3) 0%, rgba(251,146,60,0.15) 25%, rgba(251,146,60,0.3) 50%, rgba(251,146,60,0.15) 75%, rgba(251,146,60,0.3) 100%)',
                        'linear-gradient(90deg, rgba(251,146,60,0.15) 0%, rgba(251,146,60,0.3) 25%, rgba(251,146,60,0.15) 50%, rgba(251,146,60,0.3) 75%, rgba(251,146,60,0.15) 100%)',
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                    className="relative py-4 px-4 border-b-2 border-orange-500/60 overflow-hidden"
                    style={{
                      backgroundSize: '200% 100%',
                    }}
                  >
                    {/* グリッチ効果のストライプ */}
                    <div className="absolute inset-0 opacity-30">
                      <div
                        className="absolute inset-0"
                        style={{
                          backgroundImage:
                            'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(251,146,60,0.4) 10px, rgba(251,146,60,0.4) 20px)',
                        }}
                      />
                    </div>

                    <div className="relative flex items-center justify-center gap-3">
                      <motion.i
                        animate={{
                          scale: [1, 1.2, 1],
                          rotate: [0, 10, -10, 0],
                        }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="ri-signal-wifi-off-line text-orange-400 text-xl flex-shrink-0"
                      />
                      <div className="flex-1 text-center">
                        <div className="text-orange-300 text-sm font-black tracking-widest mb-1 drop-shadow-[0_0_8px_rgba(251,146,60,0.8)]">
                          ⚠️ OFFLINE MODE
                        </div>
                        <div className="text-orange-400/90 text-xs font-bold tracking-wide">
                          通信が切断されています。キャッシュされたチケットを表示中
                        </div>
                      </div>
                      <motion.i
                        animate={{
                          opacity: [1, 0.3, 1],
                        }}
                        transition={{ duration: 1, repeat: Infinity }}
                        className="ri-error-warning-line text-orange-400 text-xl flex-shrink-0"
                      />
                    </div>

                    {/* ネオンボーダー効果 */}
                    <motion.div
                      animate={{
                        boxShadow: [
                          '0 0 10px rgba(251,146,60,0.5), inset 0 0 10px rgba(251,146,60,0.2)',
                          '0 0 20px rgba(251,146,60,0.8), inset 0 0 15px rgba(251,146,60,0.3)',
                          '0 0 10px rgba(251,146,60,0.5), inset 0 0 10px rgba(251,146,60,0.2)',
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 pointer-events-none rounded-sm"
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* オフライン処理完了トースト */}
            <AnimatePresence>
              {offlineToast && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full overflow-hidden"
                >
                  <div className="py-3 px-4 bg-gradient-to-r from-cyan-500/30 via-cyan-400/40 to-cyan-500/30 border-b border-cyan-400/50 flex items-center justify-center gap-3">
                    <i className="ri-information-line text-cyan-300 text-base" />
                    <span className="text-cyan-300 text-xs font-bold tracking-wide">
                      オフラインで処理されました。通信回復後にデータが同期されます。
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* 動的時計バナー（スクショ防止） - オフラインでも動作 */}
            <motion.div
              animate={{
                background: [
                  `linear-gradient(90deg, ${statusColor}15 0%, ${statusColor}25 50%, ${statusColor}15 100%)`,
                  `linear-gradient(90deg, ${statusColor}25 0%, ${statusColor}15 50%, ${statusColor}25 100%)`,
                  `linear-gradient(90deg, ${statusColor}15 0%, ${statusColor}25 50%, ${statusColor}15 100%)`,
                ],
              }}
              transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              className="w-full py-3 border-b flex items-center justify-center gap-3"
              style={{ borderColor: `${statusColor}40` }}
            >
              <motion.i
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="ri-time-line text-lg"
                style={{ color: statusColor }}
              />
              <span className="font-mono text-sm font-bold tracking-widest" style={{ color: statusColor }}>
                {formatTime(currentTime)}
              </span>
              <motion.i
                animate={{ rotate: 360 }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                className="ri-refresh-line text-lg"
                style={{ color: statusColor }}
              />
            </motion.div>

            {/* チェックイン成功バナー */}
            <AnimatePresence>
              {showSuccess && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full overflow-hidden"
                >
                  <div className="py-3 bg-gradient-to-r from-emerald-500/30 via-emerald-400/40 to-emerald-500/30 border-b border-emerald-400/50 flex items-center justify-center gap-3">
                    <i className="ri-checkbox-circle-fill text-emerald-300 text-lg" />
                    <span className="text-emerald-300 text-sm font-bold tracking-widest">チェックイン完了！</span>
                    <i className="ri-checkbox-circle-fill text-emerald-300 text-lg" />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Status banner (入場確認済み) */}
            {isUsed && !showSuccess && (
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, type: 'spring', damping: 20 }}
                className="w-full py-3 bg-gradient-to-r from-emerald-500/20 via-emerald-500/30 to-emerald-500/20 border-b border-emerald-500/40 flex items-center justify-center gap-3"
              >
                <i className="ri-checkbox-circle-fill text-emerald-400 text-lg animate-pulse" />
                <span className="text-emerald-400 text-sm font-bold tracking-widest">入場確認済み</span>
                <i className="ri-checkbox-circle-fill text-emerald-400 text-lg animate-pulse" />
              </motion.div>
            )}

            {/* エラーバナー */}
            <AnimatePresence>
              {updateError && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full overflow-hidden"
                >
                  <div className="py-3 px-4 bg-red-500/20 border-b border-red-500/40 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0" />
                      <span className="text-red-400 text-xs font-bold tracking-wide">{updateError}</span>
                    </div>
                    <button
                      onClick={() => setUpdateError(null)}
                      className="text-red-400/60 hover:text-red-400 transition-colors cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Header */}
            <div className="relative px-6 pt-8 pb-6">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex items-center gap-4 mb-4"
              >
                <div
                  className="w-14 h-14 flex items-center justify-center rounded-2xl backdrop-blur-md"
                  style={{
                    background: `linear-gradient(135deg, ${statusColor}25, ${statusColor}10)`,
                    border: `1px solid ${statusColor}40`,
                    boxShadow: `0 0 20px ${statusColor}20`,
                  }}
                >
                  <i className="ri-ticket-2-line text-3xl" style={{ color: statusColor }} />
                </div>
                <div>
                  <h3 className="text-white font-display text-2xl tracking-wider mb-1">DIGITAL TICKET</h3>
                  <p className="text-gray-400 text-xs tracking-widest">電子チケット</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-between"
              >
                <span className="text-gray-500 text-xs tracking-wider">ステータス</span>
                <div
                  className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wider"
                  style={{
                    background: `${statusColor}20`,
                    border: `1px solid ${statusColor}50`,
                    color: statusColor,
                    boxShadow: `0 0 15px ${statusColor}20`,
                  }}
                >
                  <i className={`${statusIcon} text-sm`} />
                  {statusLabel}
                </div>
              </motion.div>
            </div>

            {/* QR Code section */}
            <div className="px-6 pb-6">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3, type: 'spring', damping: 20 }}
                className={`relative mb-6 p-6 rounded-2xl transition-all duration-500 ${isUsed ? 'opacity-60 grayscale' : ''}`}
                style={{
                  background: isUsed
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(16,185,129,0.05))'
                    : 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.08))',
                  border: `1px solid ${statusColor}40`,
                  boxShadow: `0 0 30px ${statusColor}15`,
                }}
              >
                <div className="text-center mb-5">
                  <span className="text-gray-500 text-[10px] tracking-widest block mb-2">TICKET ID</span>
                  <div className="font-mono text-2xl font-bold tracking-widest break-all" style={{ color: statusColor }}>
                    {ticket.id.toUpperCase().slice(0, 12)}
                  </div>
                </div>

                <div className="flex items-center justify-center mb-5 relative">
                  <motion.div
                    initial={{ rotate: -5, scale: 0.95 }}
                    animate={{
                      rotate: isUsed ? 0 : [0, -1, 1, -1, 0],
                      scale: isUsed ? 1 : [1, 1.02, 1, 1.02, 1],
                    }}
                    transition={{
                      delay: 0.4,
                      duration: isUsed ? 0.3 : 4,
                      repeat: isUsed ? 0 : Infinity,
                      ease: 'easeInOut',
                    }}
                    className="relative w-60 h-60 rounded-2xl p-4"
                    style={{
                      background: 'white',
                      boxShadow: isUsed ? `0 10px 40px ${statusColor}20` : `0 10px 40px ${statusColor}30`,
                    }}
                  >
                    <img src={qrCodeUrl} alt="QR Code" className="w-full h-full object-contain" />
                  </motion.div>

                  {/* CRASHED watermark */}
                  {isUsed && (
                    <motion.div
                      initial={{ scale: 0, rotate: -10 }}
                      animate={{ scale: 1, rotate: -5 }}
                      transition={{ delay: 0.5, type: 'spring', damping: 10 }}
                      className="absolute inset-0 flex items-center justify-center pointer-events-none"
                    >
                      <div
                        className="px-8 py-6 rounded-2xl border-4 rotate-[-5deg]"
                        style={{
                          background: 'rgba(16,185,129,0.4)',
                          borderColor: '#10b981',
                          boxShadow: '0 0 40px rgba(16,185,129,0.6), inset 0 0 20px rgba(16,185,129,0.3)',
                        }}
                      >
                        <span className="text-emerald-300 text-5xl font-black tracking-widest drop-shadow-[0_0_10px_rgba(16,185,129,0.8)]">
                          CRASHED
                        </span>
                        <div className="text-emerald-300 text-base font-bold tracking-widest text-center mt-2 drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]">
                          使用済み
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>

                <div className="text-center">
                  <span className="text-gray-500 text-xs tracking-wide">
                    {isUsed ? '入場確認が完了しています' : 'このコードを店舗スタッフに提示してください'}
                  </span>
                </div>
              </motion.div>

              {/* スワイプ式もぎりUI */}
              {!isUsed && isValid && (
                <motion.div
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="mb-6"
                >
                  <div className="text-center mb-3">
                    <span className="text-amber-400 text-sm font-bold tracking-wider flex items-center justify-center gap-2">
                      {isUpdating ? (
                        <>
                          <motion.i
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                            className="ri-loader-4-line"
                          />
                          チェックイン処理中...
                        </>
                      ) : (
                        <>
                          <i className="ri-arrow-right-line animate-pulse" />
                          スワイプして入場
                          <i className="ri-arrow-right-line animate-pulse" />
                        </>
                      )}
                    </span>
                  </div>

                  <div
                    ref={sliderRef}
                    className={`relative h-16 rounded-full border-2 overflow-hidden select-none ${isUpdating ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    style={{
                      background: 'linear-gradient(90deg, rgba(245,158,11,0.1), rgba(245,158,11,0.05))',
                      borderColor: isUpdating ? 'rgba(245,158,11,0.2)' : 'rgba(245,158,11,0.4)',
                    }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                  >
                    {/* プログレスバー */}
                    <motion.div
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{
                        width: `${swipeProgress}%`,
                        background:
                          swipeProgress >= 100
                            ? 'linear-gradient(90deg, #10b981, #059669)'
                            : 'linear-gradient(90deg, #f59e0b, #d97706)',
                        boxShadow:
                          swipeProgress >= 100
                            ? '0 0 20px rgba(16,185,129,0.5)'
                            : '0 0 20px rgba(245,158,11,0.3)',
                      }}
                    />

                    {/* スライダーハンドル */}
                    <motion.div
                      className="absolute inset-y-2 w-12 rounded-full flex items-center justify-center"
                      style={{
                        left: `${Math.min(swipeProgress, 100) * 0.85}%`,
                        background: swipeProgress >= 100 ? '#10b981' : '#f59e0b',
                        boxShadow:
                          swipeProgress >= 100
                            ? '0 0 30px rgba(16,185,129,0.8)'
                            : '0 0 30px rgba(245,158,11,0.6)',
                      }}
                    >
                      {isUpdating ? (
                        <motion.i
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          className="ri-loader-4-line text-white text-2xl"
                        />
                      ) : (
                        <i
                          className={`${swipeProgress >= 100 ? 'ri-checkbox-circle-fill' : 'ri-arrow-right-line'} text-white text-2xl`}
                        />
                      )}
                    </motion.div>

                    {/* テキスト */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="text-gray-400 text-sm font-bold tracking-wider">
                        {isUpdating ? '処理中...' : swipeProgress >= 100 ? '入場完了！' : '右にスワイプ'}
                      </span>
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <span className="text-gray-500 text-xs">
                      {isOffline ? '⚠️ オフラインモード：スタッフの目の前でスワイプしてください' : 'スタッフの目の前でスワイプしてください'}
                    </span>
                  </div>
                </motion.div>
              )}

              {/* Event details */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="space-y-4 mb-6"
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${statusColor}15`, border: `1px solid ${statusColor}30` }}
                  >
                    <i className="ri-calendar-event-line text-lg" style={{ color: statusColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-500 text-xs block mb-1">イベント名</span>
                    <span className="text-white text-base font-bold tracking-wide">{ticket.eventTitle}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${statusColor}15`, border: `1px solid ${statusColor}30` }}
                  >
                    <i className="ri-time-line text-lg" style={{ color: statusColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-500 text-xs block mb-1">日時</span>
                    <span className="text-white text-sm font-medium">{ticket.eventDate} {ticket.eventTime}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${statusColor}15`, border: `1px solid ${statusColor}30` }}
                  >
                    <i className="ri-map-pin-line text-lg" style={{ color: statusColor }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-gray-500 text-xs block mb-1">会場</span>
                    <span className="text-white text-sm font-medium">{ticket.eventVenue}</span>
                  </div>
                </div>

                <div
                  className="flex items-center gap-6 p-4 rounded-xl"
                  style={{ background: `${statusColor}08`, border: `1px solid ${statusColor}20` }}
                >
                  <div className="flex items-center gap-3">
                    <i className="ri-ticket-2-line text-lg" style={{ color: statusColor }} />
                    <div>
                      <span className="text-gray-500 text-xs block">購入枚数</span>
                      <span className="text-white text-lg font-bold">{ticket.ticket_count}枚</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-money-yen-circle-line text-lg" style={{ color: statusColor }} />
                    <div>
                      <span className="text-gray-500 text-xs block">合計金額</span>
                      <span className="text-white text-lg font-bold">¥{ticket.total_amount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Notice */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="p-4 rounded-xl mb-6"
                style={{
                  background: `${statusColor}08`,
                  border: `1px solid ${statusColor}25`,
                }}
              >
                <div className="flex items-start gap-3">
                  <i
                    className={`${isUsed ? 'ri-checkbox-circle-line' : 'ri-information-line'} text-base mt-0.5 flex-shrink-0`}
                    style={{ color: statusColor }}
                  />
                  <div className="text-xs text-gray-400 leading-relaxed">
                    {isUsed ? (
                      <p>このチケットは入場確認済みです。再入場については会場スタッフにお問い合わせください。</p>
                    ) : (
                      <>
                        <p className="mb-2">画面を明るくして店舗スタッフに提示してください。</p>
                        <p className="mb-2">時計が動いていることを確認し、スワイプ操作で入場処理を行います。</p>
                        {isOffline && (
                          <p className="text-orange-400 font-bold mb-2">
                            ⚠️ オフライン状態でもスワイプ処理は可能です。通信回復後に自動同期されます。
                          </p>
                        )}
                        <p className="text-amber-400 font-bold">※スクリーンショットでは入場できません</p>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Close button */}
              <button
                onClick={onClose}
                className="w-full py-4 rounded-xl bg-white/10 hover:bg-white/15 border border-white/20 hover:border-white/30 text-white text-sm font-bold tracking-wider transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                閉じる
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}